/**
 * PillarBpa — Roman numeral II, framework violation bars, top priority actions.
 *
 * Visual reference: 04-hybrid-recommended.html .pillar.bpa block.
 * Data sources:
 *   - useComplianceDashboard()  → frameworks (current score + ★ flag),
 *                                 trend history, mini-grid totals.
 *   - usePriorityActions(bpa)   → top items list (curated server-side ranking
 *                                 with Guardian Score lift estimates).
 *
 * Score shown is `bpaStarredScore` — a weighted average of frameworks the
 * tenant has opted into the Guardian Score (★). This is the same number the
 * brief and the composite-bar tile read; using `dashboardData.overallScore`
 * here would surface a different "BPA score" than the brief reasons over
 * (overallScore averages every assigned framework, ★ or not), which is
 * exactly the duality that produced the "BPA 23% vs Identity 33%, why is the
 * brief naming Identity?" confusion.
 */

import { useMemo } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer } from 'recharts'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import { useTheme } from '@/context/ThemeContext'
import { getThemeColors } from '@/lib/themeColors'
import { calculateStarredBpaScore, useComplianceDashboard } from '@/hooks/useComplianceData'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import {
  PillarShell,
  HeroMetric,
  MiniGrid,
  ActionsBlock,
  Action,
} from './PillarShell'
import { BpaEmpty } from './EmptyStates'

interface Props {
  tenantId: string | undefined
}

const MAX_FRAMEWORK_BARS = 5

export default function PillarBpa({ tenantId }: Props) {
  useTheme()
  const tc = getThemeColors()

  const { dashboardData, trendData, isLoading } = useComplianceDashboard(tenantId)
  const { actions, isLoading: actionsLoading } = usePriorityActions({
    tenantId,
    maxCount: 3,
    pillar: 'bpa',
  })

  const totalViolations = dashboardData?.totalViolations ?? 0
  const criticalCount = dashboardData?.criticalViolations ?? 0
  const highCount = dashboardData?.highViolations ?? 0

  // Empty when no framework is assigned (zero data, not "100% healthy").
  const isEmpty = !isLoading && (!dashboardData || dashboardData.frameworks.length === 0)

  // Hero score is the starred-BPA composite — same definition the brief and
  // the composite-bar tile use. Falls back to `null` when the tenant has no
  // ★ frameworks (HeroMetric handles null as "—" rather than "0%").
  const starredScore = useMemo(() => {
    if (!dashboardData?.frameworks) return null
    return calculateStarredBpaScore(
      dashboardData.frameworks.map(f => ({
        code: f.code,
        score: f.score,
        weight: f.weight,
        includeInGuardianScore: f.includeInGuardianScore,
      })),
    )
  }, [dashboardData?.frameworks])

  // Codes that contribute to the Guardian Score today. Used to filter
  // historical `frameworkScores` so the trend reflects the same starred
  // composite as the hero metric, and to filter the framework bar chart
  // below to the same set. MCSB is always-included per platform rule, so
  // it is counted as starred regardless of the flag — matching the rule
  // FrameworkSparklineCard applies (BestPracticeAnalyzerTab §6.2).
  const starredCodes = useMemo(() => {
    const codes = new Set<string>()
    for (const f of dashboardData?.frameworks ?? []) {
      const alwaysIncluded = (f.code ?? '').toUpperCase() === 'MCSB'
      if (f.includeInGuardianScore || alwaysIncluded) codes.add(f.code)
    }
    return codes
  }, [dashboardData?.frameworks])

  // Per-framework violation count chart, scoped to starred frameworks only —
  // matches the hero score, subtitle count, and violation totals. Sorted by
  // open violation count desc and capped at MAX_FRAMEWORK_BARS so the band
  // stays legible.
  const chart = useMemo(() => {
    if (!dashboardData) return [] as Array<{ code: string; violations: number; tone: 'norm' | 'warn' }>
    return dashboardData.frameworks
      .filter(fw => starredCodes.has(fw.code))
      .map(fw => ({
        code: shortFrameworkLabel(fw.code),
        violations: fw.violations ?? 0,
        tone: ((fw.criticalViolations ?? 0) > 0 ? 'warn' : 'norm') as 'norm' | 'warn',
      }))
      .sort((a, b) => b.violations - a.violations)
      .slice(0, MAX_FRAMEWORK_BARS)
  }, [dashboardData, starredCodes])

  // Trend on the starred composite: last 7-day average vs prior 7-day average.
  // Each trend point's `frameworkScores` is filtered to currently-starred
  // codes and averaged unweighted (per-day weights aren't carried in the
  // history payload — close enough for a 7v7 directional signal). Returns
  // null when there isn't enough history to compare honestly.
  const trend = useMemo(() => {
    if (isLoading || isEmpty || !trendData || trendData.length < 8 || starredCodes.size === 0) {
      return null
    }
    const sorted = [...trendData].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    )
    const starredAvgFor = (point: typeof sorted[number]): number | null => {
      const matching = (point.frameworkScores ?? []).filter(f => starredCodes.has(f.code))
      if (matching.length === 0) return null
      return matching.reduce((sum, f) => sum + f.score, 0) / matching.length
    }
    const avgWindow = (window: typeof sorted): number | null => {
      const values = window.map(starredAvgFor).filter((v): v is number => v !== null)
      if (values.length === 0) return null
      return values.reduce((s, v) => s + v, 0) / values.length
    }
    const last7 = avgWindow(sorted.slice(-7))
    const prior7 = avgWindow(sorted.slice(-14, -7))
    if (last7 === null || prior7 === null || prior7 === 0) return null
    const deltaPct = ((last7 - prior7) / prior7) * 100
    if (Math.abs(deltaPct) < 1) return { dir: 'flat', label: 'flat' } as const
    return deltaPct > 0
      ? ({ dir: 'up',   label: deltaPct.toFixed(1) } as const)
      : ({ dir: 'down', label: Math.abs(deltaPct).toFixed(1) } as const)
  }, [trendData, isLoading, isEmpty, starredCodes])

  return (
    <PillarShell
      tone="bpa"
      roman="II"
      eyebrow="Best Practice · Posture"
      Icon={ShieldCheckIcon}
      ctaLabel="Open analyzer"
      ctaHref="/security"
    >
      {isEmpty ? (
        <BpaEmpty ctaHref="/security" />
      ) : (
        <>
          <HeroMetric
            value={starredScore}
            unit="%"
            isLoading={isLoading}
            trend={trend}
          />

          <p className="mt-2 mb-6 text-[13px] leading-[1.55] text-slate-400">
            Across {starredCodes.size} enabled framework
            {starredCodes.size === 1 ? '' : 's'} in your Guardian Score.{' '}
            <strong className="font-bold text-slate-100 dark:text-slate-100">
              {totalViolations}
            </strong>{' '}
            open violation{totalViolations === 1 ? '' : 's'}, {criticalCount} critical.
          </p>

          {/* Framework bar chart — one bar per assigned framework, red where critical exist. */}
          <div className="mb-6 rounded-2xl bg-dark-800/60 px-4 py-3 dark:bg-white/[0.025]">
            <div className="mb-2 flex justify-between text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              <span>By framework</span>
              <span>open violations</span>
            </div>
            <div style={{ height: 56 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 0, right: 0, bottom: 16, left: 0 }}>
                  <Bar dataKey="violations" radius={[4, 4, 4, 4]}>
                    {chart.map((c, i) => (
                      <Cell
                        key={i}
                        fill={c.tone === 'warn' ? '#E57373' : tc.secondaryBlue}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div
              className="mt-1 grid text-center text-[9px] font-bold uppercase tracking-[0.1em] text-slate-500"
              style={{ gridTemplateColumns: `repeat(${Math.max(chart.length, 1)}, minmax(0, 1fr))` }}
            >
              {chart.map(c => (
                <span key={c.code}>{c.code}</span>
              ))}
            </div>
          </div>

          <MiniGrid
            cells={[
              { label: 'Open violations', value: String(totalViolations) },
              { label: 'Critical · High', value: `${criticalCount}`, suffix: `· ${highCount}` },
              { label: 'Frameworks', value: String(starredCodes.size) },
              {
                label: 'Last scan',
                value: formatLastScan(dashboardData?.lastEvaluatedAt),
              },
            ]}
          />

          <ActionsBlock label="Highest impact" allHref="/security" ctaLabel="What to close this week">
            {actionsLoading && actions.length === 0 ? (
              // Skeleton triplet so the items list shows movement while the
              // priority-actions fetch is in flight. Doesn't gate the rest of the pillar.
              <>
                <ActionSkeleton />
                <ActionSkeleton />
                <ActionSkeleton />
              </>
            ) : actions.length === 0 ? (
              <p className="py-3 text-[12px] text-slate-500">
                No open violations — your frameworks are clean.
              </p>
            ) : (
              actions.map(a => (
                <Action
                  key={a.id}
                  severity={a.severity}
                  title={a.title}
                  meta={a.meta}
                  guardianScoreDelta={a.guardianScoreDelta ?? null}
                  href={a.href}
                  learnMoreUrl={a.learnMoreUrl}
                />
              ))
            )}
          </ActionsBlock>
        </>
      )}
    </PillarShell>
  )
}

function formatLastScan(iso?: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  const now = Date.now()
  const mins = Math.max(0, Math.floor((now - then) / 60000))
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// Compact framework label for the chart's narrow column. Strips the
// "NIST_800_53" rev tail and trims overlong codes so all bars share the
// same visual rhythm.
function shortFrameworkLabel(code: string): string {
  const upper = code.toUpperCase()
  if (upper.startsWith('NIST_800_53')) return 'NIST 800-53'
  if (upper.startsWith('NIST_CSF'))    return 'NIST CSF'
  if (upper.length <= 8) return upper
  return upper.slice(0, 8)
}

/** Skeleton row matching the Action layout proportions while priority-actions loads. */
function ActionSkeleton() {
  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-dark-700 py-3 last:border-b-0">
      <span aria-hidden className="block h-8 w-1.5 rounded-[3px] bg-dark-700 animate-pulse" />
      <div className="min-w-0 space-y-1.5">
        <div className="h-3 w-3/4 rounded bg-dark-700 animate-pulse" />
        <div className="h-2.5 w-1/3 rounded bg-dark-700 animate-pulse" />
      </div>
      <span aria-hidden />
    </div>
  )
}
